import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ClipboardList, AlertTriangle, CheckCircle2, Eye } from "lucide-react";
import { toast } from "sonner";

const TIPO_LABEL = {
  utenti: "Utenti", anagrafiche: "Anagrafiche", pratiche: "Pratiche",
  lead: "Lead (Rubrica)", lead_campagna: "Lead (Campagna)",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function summaryText(s = {}) {
  const parts = [];
  if (s.created != null) parts.push(`${s.created} creati`);
  if (s.updated != null) parts.push(`${s.updated} aggiornati`);
  if (s.skipped != null) parts.push(`${s.skipped} ignorati`);
  if (s.skipped_duplicate != null) parts.push(`${s.skipped_duplicate} duplicati`);
  if (s.skipped_missing != null) parts.push(`${s.skipped_missing} incompleti`);
  return parts.join(" · ") || "—";
}

export default function RegistroImport() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/import-logs").then((r) => setLogs(r.data)).catch(() => toast.error("Errore caricamento registro")).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openDetail = async (id) => {
    setDetailLoading(true); setDetail({ id });
    try { const { data } = await api.get(`/import-logs/${id}`); setDetail(data); }
    catch { toast.error("Errore caricamento dettaglio"); setDetail(null); }
    finally { setDetailLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="registro-import-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2"><ClipboardList className="h-6 w-6 text-primary" /> Registro Import</h1>
        <p className="text-sm text-muted-foreground mt-1">Esito di ogni importazione: righe importate ed eventuali errori da correggere.</p>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="registro-import-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3 font-semibold">Data</th>
                <th className="px-4 py-3 font-semibold">Tipo</th>
                <th className="px-4 py-3 font-semibold">Utente</th>
                <th className="px-4 py-3 font-semibold">Esito</th>
                <th className="px-4 py-3 font-semibold">Errori</th>
                <th className="px-4 py-3 font-semibold text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} data-testid={`ri-row-${l.id}`} className="border-b border-border last:border-0 hover:bg-accent/40">
                  <td className="px-4 py-3 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                  <td className="px-4 py-3"><Badge variant="secondary">{TIPO_LABEL[l.tipo] || l.tipo}</Badge></td>
                  <td className="px-4 py-3 text-muted-foreground">{l.user_name || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{summaryText(l.summary)}</td>
                  <td className="px-4 py-3">
                    {l.error_count > 0
                      ? <span className="inline-flex items-center gap-1.5 text-amber-600 font-medium"><AlertTriangle className="h-4 w-4" />{l.error_count}</span>
                      : <span className="inline-flex items-center gap-1.5 text-green-600"><CheckCircle2 className="h-4 w-4" />0</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="outline" size="sm" data-testid={`ri-view-${l.id}`} disabled={!l.error_count} onClick={() => openDetail(l.id)}><Eye className="h-3.5 w-3.5 mr-1.5" />Errori</Button>
                  </td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">{loading ? "Caricamento…" : "Nessuna importazione registrata"}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="ri-detail-dialog">
          <DialogHeader>
            <DialogTitle>Errori import — {detail ? (TIPO_LABEL[detail.tipo] || detail.tipo || "") : ""}</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto">
            {detailLoading && <p className="text-sm text-muted-foreground py-6 text-center">Caricamento…</p>}
            {!detailLoading && detail?.errors && (
              <table className="w-full text-sm">
                <thead className="bg-muted/50 sticky top-0">
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-3 py-2 font-semibold w-16">Riga</th>
                    <th className="px-3 py-2 font-semibold w-24">Tipo</th>
                    <th className="px-3 py-2 font-semibold">Motivo</th>
                    <th className="px-3 py-2 font-semibold">Dati riga</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.errors.map((e, i) => (
                    <tr key={i} className="border-b border-border last:border-0 align-top">
                      <td className="px-3 py-2 font-mono">{e.row}</td>
                      <td className="px-3 py-2">{e.tipo === "error"
                        ? <Badge variant="destructive">Errore</Badge>
                        : <Badge variant="outline">Ignorata</Badge>}</td>
                      <td className="px-3 py-2">{e.reason}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground break-words max-w-[280px]">
                        {Object.entries(e.data || {}).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(" · ") || "—"}
                      </td>
                    </tr>
                  ))}
                  {detail.errors.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">Nessun errore</td></tr>}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
